import * as React from 'react'
import {
  useDramaticallyInefficientScopedCss,
  css,
  keyframes,
  useDramaticallyInefficientScopedKeyframes,
} from '@owl-nest/yak/extra-runtime'

type BaseProps = { className?: string }

type TransitionAwareProps<PROPS extends BaseProps> = PROPS & {
  step: 'before' | 'during' | 'after'
} & {
  ref?: React.LegacyRef<any>
  children?: React.ReactNode
}

type TransitionAwareComponent<PROPS extends BaseProps> = React.ForwardRefExoticComponent<TransitionAwareProps<PROPS>>

export function fromBottom<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition: transform 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(0, 100vh);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(0, 100vh);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'after' && afterClassName,
        ]
          .filter(Boolean)
          .join(' '),
      } as PROPS,
      props.children,
    )
  }) as TransitionAwareComponent<PROPS>
}

export function fromRight<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition: transform 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(100vw, 0);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(100vw, 0);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'after' && afterClassName,
        ]
          .filter(Boolean)
          .join(' '),
      } as PROPS,
      props.children,
    )
  }) as TransitionAwareComponent<PROPS>
}

export function fromTop<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const fadeInKeyframe = useDramaticallyInefficientScopedKeyframes(keyframes`
        from {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
    `)
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition: transform 200ms cubic-bezier(0.645, 0.045, 0.355, 1);
      animation: ${fadeInKeyframe} 1s;
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(0, -100vh);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate(-0, -100vh);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'after' && afterClassName,
        ]
          .filter(Boolean)
          .join(' '),
      } as PROPS,
      props.children,
    )
  }) as TransitionAwareComponent<PROPS>
}

export function fromLeft<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition: transform 300ms cubic-bezier(0.83, 0, 0.17, 1);
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate3d(100%, 0, 0);
    `)
    const duringClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate3d(0, 0, 0);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      transform: translate3d(100%, 0, 0);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'during' && duringClassName,
          props.step === 'after' && afterClassName,
        ]
          .filter(Boolean)
          .join(' '),
      } as PROPS,
      props.children,
    )
  }) as TransitionAwareComponent<PROPS>
}

export function fromLeftFadeIn<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition:
        opacity 300ms cubic-bezier(0.83, 0, 0.17, 1),
        transform 300ms cubic-bezier(0.83, 0, 0.17, 1);
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 0;
      transform: translate3d(100%, 0, 0);
    `)
    const duringClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 1;
      transform: translate3d(0, 0, 0);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 0;
      transform: translate3d(100%, 0, 0);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'during' && duringClassName,
          props.step === 'after' && afterClassName,
        ]
          .filter(Boolean)
          .join(' '),
      } as PROPS,
      props.children,
    )
  }) as TransitionAwareComponent<PROPS>
}

export function zoomIn<PROPS extends BaseProps>(
  Component: React.FunctionComponent<PROPS>,
): TransitionAwareComponent<PROPS> {
  // eslint-disable-next-line react/display-name
  return React.forwardRef((props, ref) => {
    const baseClass = useDramaticallyInefficientScopedCss(css`
      transition: all 300ms cubic-bezier(0.645, 0.045, 0.355, 1);
    `)
    const beforeClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 0;
      transform: scale(0.3);
    `)
    const duringClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 1;
      transform: scale(1);
    `)
    const afterClassName = useDramaticallyInefficientScopedCss(css`
      opacity: 0;
      transform: scale(0.3);
    `)

    return React.createElement(
      Component,
      {
        ...props,
        ref,
        className: [
          props.className,
          baseClass,
          props.step === 'before' && beforeClassName,
          props.step === 'during' && duringClassName,
          props.step === 'after' && afterClassName,
        ],
      } as PROPS,
      null,
    )
  }) as TransitionAwareComponent<PROPS>
}
